export const VAT_RATE = 0.18; // 18% VAT in Israel

/**
 * Calculates the total cost including VAT
 * @param {Object} params
 * @param {number} params.baseAmount - Base cost amount
 * @param {number} [params.quantity=1] - Quantity of items
 * @param {boolean} [params.includesVAT=true] - Whether baseAmount already includes VAT
 * @param {number} [params.vatRate=VAT_RATE] - VAT rate to apply (default 18%)
 * @returns {number} Total cost
 */
export const calculateTotalWithVAT = ({
  baseAmount,
  quantity = 1,
  includesVAT = true,
  vatRate = VAT_RATE,
}) => {
  if (!baseAmount) return 0;
  const vatMultiplier = includesVAT ? 1 : 1 + vatRate;
  return baseAmount * quantity * vatMultiplier;
};

/**
 * Extracts the base amount and VAT amount from a total that includes VAT
 * @param {Object} params
 * @param {number} params.totalAmount - Total amount including VAT
 * @param {number} [params.vatRate=VAT_RATE] - VAT rate to apply (default 18%)
 * @returns {Object} Object containing baseAmount and vatAmount
 */
export const extractVATFromTotal = ({ totalAmount, vatRate = VAT_RATE }) => {
  if (!totalAmount) return { baseAmount: 0, vatAmount: 0 };
  const baseAmount = totalAmount / (1 + vatRate);
  const vatAmount = totalAmount - baseAmount;
  return {
    baseAmount: Math.round(baseAmount * 100) / 100, // Round to 2 decimal places
    vatAmount: Math.round(vatAmount * 100) / 100,
  };
};

/**
 * Calculates total amount and VAT amount from a base amount
 * @param {Object} params
 * @param {number} params.baseAmount - Amount before VAT
 * @param {number} [params.vatRate=VAT_RATE] - VAT rate to apply (default 18%)
 * @returns {Object} Object containing totalAmount and vatAmount
 */
export const addVATToBase = ({ baseAmount, vatRate = VAT_RATE }) => {
  if (!baseAmount) return { totalAmount: 0, vatAmount: 0 };
  const vatAmount = baseAmount * vatRate;
  const totalAmount = baseAmount + vatAmount;
  return {
    totalAmount: Math.round(totalAmount * 100) / 100,
    vatAmount: Math.round(vatAmount * 100) / 100,
  };
};

/**
 * Gets the effective estimated cost for an item, considering selected options.
 * Cost precedence: option's default_cost > master item's estimated_cost
 *
 * @param {Object} masterItem - The master item with item_options
 * @param {Object} selectedOption - The selected option object (e.g., { group: "optionName" })
 * @returns {number|null} The effective estimated cost, or null if none
 */
export const getEffectiveEstimatedCost = (masterItem, selectedOption) => {
  // If option selected, check for its default_cost
  if (selectedOption && masterItem?.item_options?.options) {
    for (const opt of masterItem.item_options.options) {
      const group = opt.group || 'default';
      // Check if this option is selected (handle both { group: "name" } and { group: { name: "name" } } formats)
      const selectedValue = selectedOption[group];
      const optionName = typeof selectedValue === 'string' ? selectedValue : selectedValue?.name;
      if (optionName === opt.name && opt.default_cost != null) {
        return opt.default_cost;
      }
    }
  }
  // Fall back to master item's estimated_cost
  return masterItem?.estimated_cost || null;
};
